import { memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { css, Interpolation, Theme } from '@emotion/react';
import { EVENT_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarEvent, CalendarEventType, isCalendarEventType } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { selectedEventStyle } from '../styles';
import { EventCardSwitch } from './event-card-switch';
import { EventDragHandle, eventDragHandleContainerStyle } from './event-drag-handle';
import { EventShell } from './event-shell';

export const EVENT_CSS_MAP: Record<CalendarEventType, Interpolation<Theme>> = {
  appointment: css({ zIndex: EVENT_Z_INDICES.appointment, margin: '1px 2px 1px 6px', width: 'calc(100% - 8px)' }),
  scheduleDefinition: css({
    zIndex: EVENT_Z_INDICES.scheduleDefinition,
    margin: '0 0 1px 1px',
    // width is set to make the drop overlay will be of the same width as the event card
    width: 'calc(100% - 1px)',
  }),
  availabilitySlot: css({ zIndex: EVENT_Z_INDICES.availabilitySlot, margin: '0 0 1px 0' }),
  availableBlock: css({ zIndex: EVENT_Z_INDICES.availableBlock }),
  cancelledAppointment: [
    css({ zIndex: EVENT_Z_INDICES.cancelledAppointment, margin: '1px 2px 1px 6px', width: 'calc(100% - 8px)' }),
  ],
  exception: css({ zIndex: EVENT_Z_INDICES.exception }),
  scheduleRequest: css({
    zIndex: EVENT_Z_INDICES.scheduleRequest,
    margin: '1px 2px 1px 6px',
    width: 'calc(100% - 8px)',
  }),
  unavailableBlock: css({ zIndex: EVENT_Z_INDICES.unavailableBlock }),
  nonInteractiveSlot: css({ zIndex: EVENT_Z_INDICES.nonInteractiveSlot }),
  slotsStartLabel: css({ zIndex: EVENT_Z_INDICES.slotsStartLabel }),
  workingHoursV3: css({ zIndex: EVENT_Z_INDICES.workingHoursV3 }),
  nonWorkingHoursV3: css({ zIndex: EVENT_Z_INDICES.nonWorkingHoursV3 }),
};

const SELECTABLE_EVENT_TYPES = new Set<CalendarEventType>(['appointment', 'scheduleRequest', 'cancelledAppointment']);

interface Props {
  eventId: string;
  columnId?: string;
  style?: Interpolation<Theme>;
}

const EventRendererComponent = ({ eventId, columnId, style }: Props) => {
  const eventInfo = useCalendarGridStore((s) => s.eventsById[eventId]);
  const selectedEventIds = useCalendarGridStore((s) => s.selectedEventIds);
  const setSelectedEventIds = useCalendarGridStore((s) => s.setSelectedEventIds);
  const isEventDragEnabled = useCalendarGridStore((s) => s.isEventDragEnabled);
  const isEventDraggableRef = useCalendarGridStore((s) => s.isEventDraggableRef);
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);
  const eventDragPreview = useCalendarGridStore((s) => s.eventDragPreview);
  const isPendingDrop = eventDragPreview?.eventId === eventId;
  const groupId = useCalendarGridStore((s) => {
    return columnId ? s.groupIds.find((gId) => s.groupsById[gId]?.columnIds.includes(columnId)) ?? '' : '';
  });

  // canDrag is false when the predicate explicitly rejects this event
  const canDrag = isEventDragEnabled && (eventInfo ? isEventDraggableRef.current?.(eventInfo) ?? true : false);

  const { setNodeRef, attributes, listeners, transform, isDragging } = useDraggable({
    id: eventId,
    data: { columnId, groupId },
    disabled: !canDrag,
  });

  if (!eventInfo || !isCalendarEventType(eventInfo.eventType)) return null;

  const isSelectable = SELECTABLE_EVENT_TYPES.has(eventInfo.eventType);
  const isSelected = isSelectable && selectedEventIds?.includes(eventInfo.id);

  const eventCss = EVENT_CSS_MAP[eventInfo.eventType];

  // only transform must be inline — it is a dynamic dnd-kit value
  const dragTransformStyle =
    canDrag && !isDragging && transform
      ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
      : undefined;

  // eventCardOverlay applied via Emotion css; fall back to opacity:0 when not provided
  const resolvedEventCardOverlay =
    typeof dragStyles.eventCardOverlay === 'function'
      ? (dragStyles.eventCardOverlay as (e: CalendarEvent) => Interpolation<Theme>)(eventInfo)
      : dragStyles.eventCardOverlay;

  const handleSelect = isSelectable ? () => setSelectedEventIds([eventInfo.id]) : undefined;

  return (
    <CalendarDragStateContext.Provider
      value={{
        ...defaultCalendarDragState,
        isEventCardOverlay: isDragging || isPendingDrop,
        eventCardOverlayStyle: resolvedEventCardOverlay,
      }}
    >
      <EventShell
        ref={canDrag ? setNodeRef : undefined}
        id={eventInfo.id}
        startTime={eventInfo.startTime}
        duration={eventInfo.duration}
        continuesFromPreviousDay={eventInfo.continuesFromPreviousDay}
        continuesToNextDay={eventInfo.continuesToNextDay}
        style={dragTransformStyle}
        css={[eventCss, style, isSelected && selectedEventStyle, eventDragHandleContainerStyle(false, isDragging)]}
        data-dragging={isDragging ? 'true' : undefined}
        isSelectable={isSelectable}
        isSelected={isSelected}
        onSelect={handleSelect}
        {...(canDrag ? attributes : {})}
      >
        {/* Drag handle — only element with dnd-kit listeners; shown on hover */}
        {canDrag && <EventDragHandle listeners={listeners} />}
        <EventCardContent eventInfo={eventInfo} />
      </EventShell>
    </CalendarDragStateContext.Provider>
  );
};

export const EventCardContent = ({ eventInfo }: { eventInfo: CalendarEvent }) => {
  const renderEventCardRef = useCalendarGridStore((s) => s.renderEventCardRef);

  return (
    <>
      {renderEventCardRef?.current && typeof renderEventCardRef.current === 'function' ? (
        renderEventCardRef.current(eventInfo)
      ) : (
        <EventCardSwitch event={eventInfo} />
      )}
    </>
  );
};

export const EventRenderer = memo(EventRendererComponent);
